import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FileUpload,
  type FileUploadFileAcceptDetails,
  type FileUploadFileRejectDetails,
  HStack,
  Icon,
  Image,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ArrowUpTrayIcon, FilmIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { Navigate, NavLink, useLocation, useParams } from 'react-router-dom';
import AuthHeader from '@/components/auth-header';
import { ProgressBar } from '@/components/ui/progress-bar';
import { useAuth } from '@/lib/hooks/use-auth';
import {
  AssistantMediaKind,
  AssistantUpload as UploadLink,
  AssistantUploadedFile,
} from '@/lib/types';
import { getAssistantUpload, uploadAssistantFile } from '@/utils/assistant-uploads';
import { clearReturnTo, rememberReturnTo } from '@/utils/return-to';

// The types the API keeps (baseapi app/media sniffs the bytes as well).
// Listing them also makes iOS hand over HEIC photos as JPEG.
const formats: Record<AssistantMediaKind, Record<string, string[]>> = {
  image: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
  },
  video: {
    'video/mp4': ['.mp4'],
    'video/webm': ['.webm'],
    'video/quicktime': ['.mov'],
  },
};

const formatNames: Record<AssistantMediaKind, string> = {
  image: 'JPEG, PNG, WebP or GIF',
  video: 'MP4, WebM or MOV',
};

const MB = 1024 * 1024;

// How long an upload link works (media.UploadTTL in baseapi).
const LINK_LIFETIME_MS = 30 * 60 * 1000;

const formatSize = (bytes: number) =>
  bytes < MB ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${+(bytes / MB).toFixed(1)} MB`;

const kindOf = (file: File): AssistantMediaKind =>
  file.type.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(file.name) ? 'video' : 'image';

type ItemStatus = 'queued' | 'uploading' | 'done' | 'failed';

// One file the user picked on this page.
interface Item {
  id: number;
  file: File;
  kind: AssistantMediaKind;
  status: ItemStatus;
  /** Share of the file sent, 0-100. */
  progress: number;
  error?: string;
  /** Interrupted or failed on our side, so sending it again may work. */
  retryable?: boolean;
  result?: AssistantUploadedFile;
}

const patchItem = (id: number, patch: Partial<Item>) => (items: Item[]) =>
  items.map((i) => (i.id === id ? { ...i, ...patch } : i));

// Held empty so the drop zone reports only newly picked files; the queue
// lives in this page's state.
const noFiles: File[] = [];

type PageState = 'loading' | 'ready' | 'expired' | 'forbidden' | 'session' | 'failed';

const problems: Record<
  Exclude<PageState, 'loading' | 'ready'>,
  { title: string; message: string }
> = {
  expired: {
    title: 'Upload link expired',
    message: 'This upload link has expired or does not exist. Ask your assistant for a new one.',
  },
  forbidden: {
    title: 'Wrong account',
    message:
      'This upload link was made for a different Athlete Era account. Sign in as that account, or ask your assistant for a new link.',
  },
  session: {
    title: 'Signed out',
    message: 'Your session has ended. Sign in again to use this upload link.',
  },
  failed: {
    title: 'Something went wrong',
    message: 'The upload link could not be loaded. Please try again in a moment.',
  },
};

// AssistantUpload is the page behind an upload link. An assistant (Claude,
// ChatGPT) connected over MCP creates the link for one content item or course
// cover; the user opens it signed in and adds files from their device. Each
// file is attached as soon as it finishes uploading.
const AssistantUpload = () => {
  const { uploadId = '' } = useParams();
  const location = useLocation();
  const auth = useAuth();

  const [link, setLink] = useState<UploadLink | null>(null);
  const [state, setState] = useState<PageState>('loading');
  // When the link stops working, by this page's clock.
  const [deadline, setDeadline] = useState(0);
  const [expired, setExpired] = useState(false);
  const [items, setItems] = useState<Item[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const nextId = useRef(1);
  const inflight = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    // We are the return-to destination; stop the stored path from steering
    // later logins in this tab.
    clearReturnTo();
    let cancelled = false;
    (async () => {
      try {
        const l = await getAssistantUpload(uploadId);
        if (cancelled) return;
        setLink(l);
        setDeadline(Date.now() + l.expires_in * 1000);
        setState('ready');
      } catch (e: any) {
        if (cancelled) return;
        const status = e?.response?.status;
        setState(
          status === 404
            ? 'expired'
            : status === 403
              ? 'forbidden'
              : status === 401
                ? 'session'
                : 'failed',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uploadId, auth.isAuthenticated]);

  useEffect(() => {
    if (!deadline) return;
    const t = window.setTimeout(() => setExpired(true), Math.max(0, deadline - Date.now()));
    return () => window.clearTimeout(t);
  }, [deadline]);

  // Send queued files one at a time: each upload rewrites the item's media
  // list, so parallel uploads could drop each other's files.
  useEffect(() => {
    if (state !== 'ready' || items.some((i) => i.status === 'uploading')) return;
    const next = items.find((i) => i.status === 'queued');
    if (!next) return;
    if (expired) {
      setItems((prev) =>
        prev.map((i) =>
          i.status === 'queued'
            ? { ...i, status: 'failed', error: 'The link expired before this file was sent.' }
            : i,
        ),
      );
      return;
    }
    const ctrl = new AbortController();
    inflight.current = ctrl;
    setItems(patchItem(next.id, { status: 'uploading', progress: 0, error: undefined }));
    let shown = 0;
    uploadAssistantFile(
      uploadId,
      next.file,
      (percent) => {
        const p = Math.floor(percent);
        if (p === shown) return;
        shown = p;
        setItems(patchItem(next.id, { progress: p }));
      },
      ctrl.signal,
    )
      .then((result) => setItems(patchItem(next.id, { status: 'done', progress: 100, result })))
      .catch((e: any) => {
        if (ctrl.signal.aborted) return;
        const status: number | undefined = e?.response?.status;
        if (status === 401) {
          setState('session');
          return;
        }
        if (status === 404) setExpired(true);
        setItems(
          patchItem(next.id, {
            status: 'failed',
            error:
              e?.response?.data?.error ||
              (status
                ? 'The file could not be added.'
                : 'The upload was interrupted. Check your connection and try again.'),
            retryable: !status || status >= 500,
          }),
        );
      })
      .finally(() => {
        if (inflight.current === ctrl) inflight.current = null;
      });
  }, [items, state, expired, uploadId]);

  // Stop a running upload when the page goes away.
  useEffect(() => () => inflight.current?.abort(), []);

  const busy = items.some((i) => i.status === 'queued' || i.status === 'uploading');

  // Closing the tab mid-upload loses the file; ask first.
  useEffect(() => {
    if (!busy) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  if (!auth.isAuthenticated) {
    // Come back here after login, for as long as the link can still work.
    rememberReturnTo(location.pathname + location.search, LINK_LIFETIME_MS);
    return <Navigate to='/auth/login' replace />;
  }

  if (state !== 'loading' && state !== 'ready') {
    const { title, message } = problems[state];
    return (
      <Stack gap={6}>
        <AuthHeader title={title} />
        <Text textAlign='center' color='fg.muted'>
          {message}
        </Text>
        <Stack align='center' gap={3}>
          {(state === 'forbidden' || state === 'session') && (
            // Logging out sends us to login, which brings the user back here.
            <Button colorPalette='brand' onClick={auth.logout}>
              {state === 'forbidden' ? 'Sign in with another account' : 'Sign in again'}
            </Button>
          )}
          <Button variant='outline' asChild>
            <NavLink to='/'>Go to Athlete Era</NavLink>
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (state === 'loading' || !link) {
    return (
      <Stack gap={6} align='center'>
        <AuthHeader title='Upload files' />
        <Spinner size='lg' />
      </Stack>
    );
  }

  const isCover = link.target === 'course_cover';
  const kinds = link.accepts;
  const limit = (kind: AssistantMediaKind) =>
    kind === 'video' ? (link.max_video_bytes ?? 0) : link.max_image_bytes;
  // A cover takes one image at a time and each replaces the last; content
  // links add up to max_files in total.
  const used = link.uploaded.length + items.filter((i) => i.status !== 'failed').length;
  const remaining = isCover ? 1 : Math.max(0, link.max_files - used);
  const full = !isCover && remaining === 0;

  const accept = (details: FileUploadFileAcceptDetails) => {
    if (!details.files.length) return;
    setRejected([]);
    const picked = details.files.slice(0, remaining);
    const skipped = details.files.slice(remaining);
    if (skipped.length) {
      setRejected([
        `Only ${remaining} more ${remaining === 1 ? 'file' : 'files'} can be added with this link, so ${skipped.length} ${skipped.length === 1 ? 'was' : 'were'} left out.`,
      ]);
    }
    setItems((prev) => [
      ...prev,
      ...picked.map((file): Item => {
        const kind = kindOf(file);
        const max = limit(kind);
        const tooBig = file.size > max;
        return {
          id: nextId.current++,
          file,
          kind,
          status: tooBig ? 'failed' : 'queued',
          progress: 0,
          error: tooBig
            ? `${kind === 'video' ? 'Videos' : 'Images'} can be up to ${formatSize(max)}; this file is ${formatSize(file.size)}.`
            : undefined,
        };
      }),
    ]);
  };

  const reject = (details: FileUploadFileRejectDetails) => {
    if (!details.files.length) return;
    setRejected(
      details.files.map(({ file, errors }) =>
        errors.includes('TOO_MANY_FILES')
          ? isCover
            ? 'Choose one image for the cover.'
            : `One link can add up to ${link.max_files} files.`
          : `${file.name} is not a supported file type.`,
      ),
    );
  };

  const retry = (id: number) =>
    setItems(patchItem(id, { status: 'queued', progress: 0, error: undefined, retryable: false }));

  const done = items.filter((i) => i.status === 'done').length;
  const until = new Date(deadline).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <Stack gap={6} py={8}>
      <AuthHeader title={isCover ? 'Change course cover' : 'Add images and videos'} />

      <Box borderWidth={1} borderColor='border' borderRadius='lg' p={4} bg='bg.panel'>
        <Text textStyle='eyebrow'>{isCover ? 'Course' : 'Content item'}</Text>
        <Text fontSize='lg' fontWeight='600' color='fg.strong' mt={1}>
          {link.target_name}
        </Text>
      </Box>

      <FileUpload.Root
        maxW='full'
        alignItems='stretch'
        acceptedFiles={noFiles}
        accept={Object.assign({}, ...kinds.map((k) => formats[k]))}
        maxFiles={isCover ? 1 : link.max_files}
        disabled={expired || full}
        onFileAccept={accept}
        onFileReject={reject}
      >
        <FileUpload.HiddenInput />
        <FileUpload.Dropzone minHeight='140px' _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}>
          <Icon as={ArrowUpTrayIcon} w={5} h={5} color='fg.muted' />
          <FileUpload.DropzoneContent>
            <Box>
              {isCover
                ? 'Drop an image here, or click to choose one'
                : 'Drop images or videos here, or click to choose'}
            </Box>
            {kinds.map((k) => (
              <Box key={k} color='fg.muted'>
                {formatNames[k]} up to {formatSize(limit(k))}
              </Box>
            ))}
          </FileUpload.DropzoneContent>
        </FileUpload.Dropzone>
      </FileUpload.Root>

      {(expired || full || rejected.length > 0) && (
        <Stack gap={1}>
          {expired ? (
            <Text fontSize='sm' color='fg.muted'>
              This upload link has expired. Ask your assistant for a new one to add more.
            </Text>
          ) : (
            full && (
              <Text fontSize='sm' color='fg.muted'>
                This link has added all the files it can. Ask your assistant for a new link to add
                more.
              </Text>
            )
          )}
          {rejected.map((r, n) => (
            <Text key={n} fontSize='sm' color='danger'>
              {r}
            </Text>
          ))}
        </Stack>
      )}

      {items.length > 0 && (
        <Stack as='ul' gap={3} pl={0} listStyleType='none' aria-live='polite'>
          {items.map((i) => (
            <HStack as='li' key={i.id} gap={3} align='center'>
              <Thumb kind={i.kind} file={i.result} />
              <Box flex={1} minW={0}>
                <Text textStyle='ui' color='fg.strong' truncate>
                  {i.file.name}
                </Text>
                {i.status === 'uploading' ? (
                  <ProgressBar
                    mt={1}
                    value={i.progress}
                    label={formatSize(i.file.size)}
                    valueLabel={i.progress >= 100 ? 'Processing…' : `${i.progress}%`}
                  />
                ) : (
                  <Text
                    fontSize='sm'
                    color={
                      i.status === 'failed'
                        ? 'danger'
                        : i.status === 'done'
                          ? 'success'
                          : 'fg.muted'
                    }
                  >
                    {i.status === 'queued'
                      ? `Waiting · ${formatSize(i.file.size)}`
                      : i.status === 'done'
                        ? isCover
                          ? 'Set as the course cover'
                          : 'Added'
                        : i.error}
                  </Text>
                )}
              </Box>
              {i.status === 'failed' && i.retryable && !expired && (
                <Button size='xs' variant='outline' onClick={() => retry(i.id)}>
                  Retry
                </Button>
              )}
            </HStack>
          ))}
        </Stack>
      )}

      {link.uploaded.length > 0 && (
        <Stack gap={2}>
          <Text fontSize='sm' color='fg.muted'>
            Already added with this link
          </Text>
          <HStack gap={2} wrap='wrap'>
            {link.uploaded.map((f, n) => (
              <Thumb key={n} kind={f.kind} file={f} />
            ))}
          </HStack>
        </Stack>
      )}

      {(!expired || done > 0) && (
        <Stack gap={1}>
          {!expired && (
            <Text fontSize='sm' color='fg.muted' textAlign='center'>
              {isCover
                ? 'The image replaces the current cover as soon as it finishes uploading.'
                : 'Files are added as soon as they finish uploading.'}{' '}
              This link works until {until}.
            </Text>
          )}
          {done > 0 && !busy && (
            <Text fontSize='sm' color='fg.muted' textAlign='center'>
              You can go back to your assistant now.
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

// Thumb shows an uploaded file's thumbnail, or an icon until there is one.
function Thumb({ kind, file }: { kind: AssistantMediaKind; file?: AssistantUploadedFile }) {
  // Content files come with a thumbnail; a course cover is its own image.
  const src = file?.thumbnail_url || (kind === 'image' ? file?.file_url : undefined);
  return (
    <Box
      boxSize='48px'
      flexShrink={0}
      borderRadius='md'
      overflow='hidden'
      bg='bg.subtle'
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      {src ? (
        <Image src={src} alt='' boxSize='100%' objectFit='cover' />
      ) : (
        <Icon as={kind === 'video' ? FilmIcon : PhotoIcon} w={5} h={5} color='fg.muted' />
      )}
    </Box>
  );
}

export default AssistantUpload;
